import React from "react";
import { InputAdornment, TextField } from "@mui/material";
import { Check, ErrorOutline } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/vi";
import { commonFieldSx } from "./dropdownStyles";

const statusIcon = {
  success: <Check fontSize="small" sx={{ color: "#20a354" }} />,
  error: <ErrorOutline fontSize="small" sx={{ color: "#d32f2f" }} />,
};

export default function AppTextField({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  error = false,
  success = false,
  helperText,
  placeholder,
  type = "text",
  defaultToday = false,
  multiline = false,
  minRows,
  maxRows,
  fullWidth = true,
  className,
  sx,
  name,
  inputProps,
  InputProps,
  startIcon,
  endIcon,
  showCount = false,
  maxLength,
  ...props
}) {
  const resolvedError = Boolean(error);
  const resolvedSuccess = success && !resolvedError;
  const countText = showCount && maxLength ? `${String(value ?? "").length} / ${maxLength}` : "";
  const resolvedHelperText = helperText || countText || undefined;
  const resolvedEndIcon = endIcon || (resolvedError ? statusIcon.error : resolvedSuccess ? statusIcon.success : null);
  const valueProps = value === undefined ? {} : { value: value ?? "" };
  // const dateValue = value ? dayjs(value) : null;
  const dateValue = value
    ? dayjs(value)
    : defaultToday
      ? dayjs()
      : null;

  if (type === "date") {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
        <DatePicker
          label={label}
          value={dateValue?.isValid() ? dateValue : null}
          onChange={(nextValue) => {
            onChange?.(nextValue?.isValid?.() ? nextValue.format("YYYY-MM-DD") : "", nextValue);
          }}
          format="DD/MM/YYYY"
          disabled={disabled}
          dayOfWeekFormatter={(day) => day.format("dd")}
          slotProps={{
            textField: {
              ...props,
              name,
              className,
              required,
              error: resolvedError,
              helperText: resolvedHelperText,
              placeholder: placeholder || "dd/MM/yyyy",
              fullWidth,
              size: "small",
              inputProps: {
                maxLength,
                ...inputProps,
                ...props.inputProps,
              },
              FormHelperTextProps: {
                sx: countText && !helperText ? { textAlign: "right", color: "#4f8edb" } : undefined,
                ...props.FormHelperTextProps,
              },
              sx: {
                ...commonFieldSx,
                ...(resolvedSuccess ? {
                  "& .MuiOutlinedInput-root fieldset": { borderColor: "#78c785" },
                  "& .MuiInputLabel-root.MuiInputLabel-shrink": { color: "#2f9c4f" },
                } : {}),
                ...sx,
              },
            },
          }}
        />
      </LocalizationProvider>
    );
  }

  return (
    <TextField
      {...props}
      name={name}
      className={className}
      label={label}
      {...valueProps}
      onChange={(event) => onChange?.(event.target.value, event)}
      required={required}
      disabled={disabled}
      error={resolvedError}
      helperText={resolvedHelperText}
      placeholder={placeholder}
      type={type}
      multiline={multiline}
      minRows={multiline ? minRows || 3 : minRows}
      maxRows={maxRows}
      fullWidth={fullWidth}
      size="small"
      sx={{
        ...commonFieldSx,
        ...(resolvedSuccess ? {
          "& .MuiOutlinedInput-root fieldset": { borderColor: "#78c785" },
          "& .MuiInputLabel-root.MuiInputLabel-shrink": { color: "#2f9c4f" },
        } : {}),
        ...sx,
      }}
      inputProps={{
        maxLength,
        ...inputProps,
      }}
      InputLabelProps={type === "date" ? { shrink: true } : props.InputLabelProps}
      InputProps={{
        ...InputProps,
        startAdornment: startIcon ? (
          <InputAdornment position="start">{startIcon}</InputAdornment>
        ) : InputProps?.startAdornment,
        endAdornment: resolvedEndIcon ? (
          <InputAdornment position="end">{resolvedEndIcon}</InputAdornment>
        ) : InputProps?.endAdornment,
      }}
      FormHelperTextProps={{
        sx: countText && !helperText ? { textAlign: "right", color: "#4f8edb" } : undefined,
        ...props.FormHelperTextProps,
      }}
    />
  );
}
